import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { getWithdrawalLimits, updateWithdrawalLimits } from '../../services/withdrawalLimitService';

function WithdrawalLimitManagement() {
  const { isAdmin } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [limits, setLimits] = useState({
    minAmount: 100,
    maxAmount: 10000,
    maxDailyAmount: 10000
  });
  
  const [originalLimits, setOriginalLimits] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      loadLimits();
    }
  }, [isAdmin]);

  const loadLimits = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await getWithdrawalLimits();
      setLimits({
        minAmount: data.minAmount || 100,
        maxAmount: data.maxAmount || 10000,
        maxDailyAmount: data.maxDailyAmount || 10000
      });
      setOriginalLimits(JSON.parse(JSON.stringify({
        minAmount: data.minAmount || 100,
        maxAmount: data.maxAmount || 10000,
        maxDailyAmount: data.maxDailyAmount || 10000
      })));
    } catch (err) {
      console.error('Error loading withdrawal limits:', err);
      setError('Failed to load withdrawal limit configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleLimitChange = (field, value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) && value !== '') {
      return; // Don't update if invalid number
    }
    
    setLimits(prev => ({
      ...prev,
      [field]: value === '' ? '' : numValue
    }));
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validate limits
      if (limits.minAmount !== '' && limits.minAmount < 100) {
        throw new Error('Minimum withdrawal amount cannot be less than $100');
      }

      if (limits.maxAmount !== '' && limits.maxAmount < 100) {
        throw new Error('Maximum withdrawal amount cannot be less than $100');
      }

      if (limits.minAmount !== '' && limits.maxAmount !== '' && limits.minAmount > limits.maxAmount) {
        throw new Error('Minimum withdrawal amount cannot be greater than maximum withdrawal amount');
      }

      if (limits.maxDailyAmount !== '' && limits.maxDailyAmount < 100) {
        throw new Error('Maximum daily withdrawal amount cannot be less than $100');
      }

      // Prepare update data (only include fields that have values)
      const updateData = {};
      if (limits.minAmount !== '') updateData.minAmount = limits.minAmount;
      if (limits.maxAmount !== '') updateData.maxAmount = limits.maxAmount;
      if (limits.maxDailyAmount !== '') updateData.maxDailyAmount = limits.maxDailyAmount;

      await updateWithdrawalLimits(updateData);
      
      setSuccess('Withdrawal limits updated successfully!');
      await loadLimits(); // Reload to get updated data with timestamps
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving withdrawal limits:', err);
      setError(err.message || 'Failed to update withdrawal limits');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!originalLimits) return false;
    return (
      limits.minAmount !== originalLimits.minAmount ||
      limits.maxAmount !== originalLimits.maxAmount ||
      limits.maxDailyAmount !== originalLimits.maxDailyAmount
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-currency-dollar me-2"></i>
              Withdrawal Limit Management
            </h2>
            <button
              className="btn btn-outline-secondary"
              onClick={loadLimits}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="bi bi-check-circle me-2"></i>
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          {/* Withdrawal Limits Configuration */}
          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-gear me-2"></i>
                Withdrawal Limits Settings
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                {/* Minimum Withdrawal Amount */}
                <div className="col-md-4 mb-3">
                  <label htmlFor="minAmount" className="form-label">
                    Minimum Withdrawal Amount (USDT) <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      id="minAmount"
                      value={limits.minAmount}
                      onChange={(e) => handleLimitChange('minAmount', e.target.value)}
                      min="100"
                      step="1"
                      required
                      disabled={saving}
                    />
                  </div>
                  <small className="text-muted">
                    <strong>Fixed at $100 minimum.</strong> Users cannot withdraw less than this amount.
                  </small>
                </div>

                {/* Maximum Withdrawal Amount */}
                <div className="col-md-4 mb-3">
                  <label htmlFor="maxAmount" className="form-label">
                    Maximum Withdrawal Amount (USDT) <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      id="maxAmount"
                      value={limits.maxAmount}
                      onChange={(e) => handleLimitChange('maxAmount', e.target.value)}
                      min="100"
                      step="1"
                      required
                      disabled={saving}
                    />
                  </div>
                  <small className="text-muted">
                    Maximum amount a user can withdraw in a single transaction.
                  </small>
                </div>

                {/* Maximum Daily Withdrawal Amount */}
                <div className="col-md-4 mb-3">
                  <label htmlFor="maxDailyAmount" className="form-label">
                    Maximum Daily Withdrawal (USDT) <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input
                      type="number"
                      className="form-control"
                      id="maxDailyAmount"
                      value={limits.maxDailyAmount}
                      onChange={(e) => handleLimitChange('maxDailyAmount', e.target.value)}
                      min="100"
                      step="1"
                      required
                      disabled={saving}
                    />
                  </div>
                  <small className="text-muted">
                    Maximum total amount a user can withdraw per day.
                  </small>
                </div>
              </div>

              {/* Current Settings Display */}
              {originalLimits && (
                <div className="mt-4 p-3 bg-light rounded">
                  <h6 className="mb-3">
                    <i className="bi bi-info-circle me-2"></i>
                    Current Settings
                  </h6>
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Minimum:</strong> ${originalLimits.minAmount} USDT
                    </div>
                    <div className="col-md-4">
                      <strong>Maximum:</strong> ${originalLimits.maxAmount} USDT
                    </div>
                    <div className="col-md-4">
                      <strong>Daily Limit:</strong> ${originalLimits.maxDailyAmount} USDT
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-4">
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || !hasChanges()}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
                {hasChanges() && (
                  <button
                    className="btn btn-outline-secondary ms-2"
                    onClick={loadLimits}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Information Card */}
          <div className="card">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                Information
              </h5>
            </div>
            <div className="card-body">
              <ul className="mb-0">
                <li>
                  <strong>Minimum Withdrawal:</strong> Fixed at $100. This cannot be changed below $100 as per system requirements.
                </li>
                <li>
                  <strong>Maximum Withdrawal:</strong> Configurable limit for single transaction withdrawals.
                </li>
                <li>
                  <strong>Daily Limit:</strong> Maximum total amount a user can withdraw in a 24-hour period.
                </li>
                <li>
                  <strong>Validation:</strong> All limits are validated on both frontend and backend to ensure security.
                </li>
                <li>
                  <strong>User Experience:</strong> Users will see real-time validation messages when entering withdrawal amounts.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WithdrawalLimitManagement;

